import React from "react";
import dayjs from "dayjs";
import { formatEmpIdForDisplay } from "./nocUtils";

interface NocWorkHistoryCardProps {
  nocData: any;
}

export const NocWorkHistoryCard: React.FC<NocWorkHistoryCardProps> = ({ nocData }) => {
  let rawList: any[] = [
    ...(Array.isArray(nocData?.remarks_data) && nocData.remarks_data.length > 0
      ? nocData.remarks_data
      : Array.isArray(nocData?.history) && nocData.history.length > 0
        ? nocData.history
        : Array.isArray(nocData?.work_history) && nocData.work_history.length > 0
          ? nocData.work_history
          : Array.isArray(nocData?.remarksHistory) && nocData.remarksHistory.length > 0
            ? nocData.remarksHistory
            : [])
  ].map((item: any) => ({ ...item }));

  const applicantId = String(
    nocData?.empId ||
    nocData?.empID ||
    nocData?.employee?.empId ||
    nocData?.employee?.empID ||
    ""
  ).replace(/^0+/, "");

  const hasEmployeeEntry = rawList.some((item: any) => {
    const historyUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
    return (
      item?.usertype === "E" ||
      item?.usertype === "Employee" ||
      item?.approverName === "User" ||
      (applicantId && historyUserId === applicantId)
    );
  });

  const employeeRemarks = (() => {
    const candidates = [
      nocData?.anyRemarks,
      nocData?.any_remarks,
      nocData?.employeeRemarks,
      nocData?.empRemarks,
      nocData?.employee_remarks,
      nocData?.applicantRemarks,
      nocData?.userRemarks,
    ];
    for (const cand of candidates) {
      if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
        return cand.trim();
      }
    }
    return "";
  })();

  const employeeTimestamp =
    nocData?.requestCreationDate ||
    nocData?.created_at ||
    nocData?.createdAt ||
    nocData?.sap_created_at ||
    nocData?.sapCreatedAt ||
    nocData?.appliedDate ||
    null;

  const employeeName = (() => {
    if (nocData?.employee) {
      const fname = nocData.employee.firstname || nocData.employee.firstName || "";
      const lname = nocData.employee.lsastname || nocData.employee.lastname || nocData.employee.lastName || "";
      const full = `${fname} ${lname}`.trim();
      if (full) return full;
    }
    return nocData?.employeeName || nocData?.empName || "Employee";
  })();

  const employeeUserId =
    nocData?.empId || nocData?.empID || nocData?.employee?.empId || nocData?.employee?.empID || "";

  if (hasEmployeeEntry) {
    rawList = rawList.map((item: any) => {
      const historyUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
      const isEmp =
        item?.usertype === "E" ||
        item?.usertype === "Employee" ||
        item?.approverName === "User" ||
        (applicantId && historyUserId === applicantId);
      // Only set employeeRemarks if item.remarks was empty AND actual employeeRemarks exist from applicant
      if (isEmp && !item?.remarks && employeeRemarks) {
        return { ...item, remarks: employeeRemarks };
      }
      return { ...item };
    });
  } else if (nocData && (employeeUserId || employeeName !== "Employee" || nocData?.id || nocData?.referenceNumber)) {
    rawList.push({
      usertype: "Employee",
      approverUserID: employeeUserId,
      userId: employeeUserId,
      empID: employeeUserId,
      approverName: employeeName,
      name: employeeName,
      remarks: employeeRemarks,
      createdAt: employeeTimestamp,
      isEmployee: true,
    });
  }

  if (rawList.length === 0) return null;

  // Reconcile latest approver remarks from nocData root if duplicated or displaced by backend
  const approverEntries = rawList.filter((item: any) => {
    const historyUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
    const isEmp =
      item?.usertype === "E" ||
      item?.usertype === "Employee" ||
      item?.approverName === "User" ||
      (applicantId && historyUserId === applicantId);
    return !isEmp;
  });

  if (approverEntries.length > 0) {
    // Sort chronologically ascending (oldest to newest) to find previous and latest
    approverEntries.sort((a: any, b: any) => {
      const timeA = new Date(a?.createdAt || a?.created_at || a?.timeStamp || a?.timestamp || a?.date || 0).getTime();
      const timeB = new Date(b?.createdAt || b?.created_at || b?.timeStamp || b?.timestamp || b?.date || 0).getTime();
      if (timeA !== timeB) return timeA - timeB;
      const lvlA = Number(a?.approverLevel ?? 0);
      const lvlB = Number(b?.approverLevel ?? 0);
      return lvlA - lvlB;
    });

    const latestApprover = approverEntries[approverEntries.length - 1];
    const prevApprover = approverEntries.length > 1 ? approverEntries[approverEntries.length - 2] : null;

    const rootRemarks =
      nocData?.remarks && nocData.remarks !== "X" && String(nocData.remarks).trim() !== ""
        ? String(nocData.remarks).trim()
        : null;

    const rootRemarks1 =
      nocData?.Remarks1 && nocData.Remarks1 !== "X" && String(nocData.Remarks1).trim() !== ""
        ? String(nocData.Remarks1).trim()
        : null;

    const latestRemark = String(latestApprover?.remarks || latestApprover?.remark || "").trim();
    const prevRemark = prevApprover ? String(prevApprover?.remarks || prevApprover?.remark || "").trim() : "";

    // Approver remarks should remain as entered and never fallback to employee root remarks
  }

  // Deduplicate
  const seen = new Set();
  const uniqueRemarks = rawList.filter((item: any) => {
    const uid = String(item?.approverUserID || item?.userId || item?.empID || "").trim();
    const rem = String(item?.remarks || item?.remark || "").trim();
    const ts = String(item?.createdAt || item?.created_at || item?.timeStamp || item?.timestamp || item?.date || "").trim();
    const key = `${uid}_${rem}_${ts}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Sort descending
  const sortedRemarks = [...uniqueRemarks].sort((a: any, b: any) => {
    const timeA = new Date(a?.createdAt || a?.created_at || a?.timeStamp || a?.timestamp || a?.date || 0).getTime();
    const timeB = new Date(b?.createdAt || b?.created_at || b?.timeStamp || b?.timestamp || b?.date || 0).getTime();
    const validTimeA = isNaN(timeA) ? 0 : timeA;
    const validTimeB = isNaN(timeB) ? 0 : timeB;
    if (validTimeA === 0 && validTimeB === 0) return 0;
    if (validTimeA === 0) return 1;
    if (validTimeB === 0) return -1;
    return validTimeB - validTimeA;
  });

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div className="card-header bg-light">
        <h5 className="card-title mb-0">
          <i className="ti ti-history me-2 text-primary"></i>
          Previous Remarks
        </h5>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>User Type</th>
                <th>User ID</th>
                <th>User Name</th>
                <th>Remarks</th>
                <th>Time Stamp</th>
              </tr>
            </thead>
            <tbody>
              {sortedRemarks.map((history: any, index: number) => {
                const historyUserId = String(history?.approverUserID || history?.userId || history?.empID || "").replace(/^0+/, "");
                const isEmployee =
                  history?.usertype === "E" ||
                  history?.usertype === "Employee" ||
                  history?.approverName === "User" ||
                  (applicantId && historyUserId === applicantId);

                const userType = isEmployee ? "Employee" : "Approver";

                const userName = (() => {
                  if (isEmployee) {
                    if (nocData?.employee) {
                      const fname = nocData.employee.firstname || "";
                      const lname = nocData.employee.lsastname || "";
                      const full = `${fname} ${lname}`.trim();
                      if (full) return full;
                    }
                    if (history?.userName && history.userName !== "Approver") return history.userName;
                    if (history?.empName) return history.empName;
                    if (history?.approverName && history.approverName !== "User" && history.approverName !== "Approver") return history.approverName;
                    return "Employee";
                  } else {
                    if (history?.approverName && history.approverName !== "User") return history.approverName;
                    if (history?.userName) return history.userName;
                    if (history?.name) return history.name;
                    if (history?.history_approver) {
                      const fname = history.history_approver.firstname || history.history_approver.first_name || "";
                      const lname = history.history_approver.lsastname || history.history_approver.last_name || "";
                      const full = `${fname} ${lname}`.trim();
                      if (full) return full;
                    }
                    return "Approver";
                  }
                })();

                const displayUserId =
                  formatEmpIdForDisplay(
                    history?.history_approver?.empId ||
                    history?.approverUserID ||
                    history?.userId ||
                    history?.empID ||
                    (isEmployee ? (applicantId || nocData?.empID || nocData?.empId) : "")
                  ) || "-";

                const formattedTimestamp = (() => {
                  const rawDate = history?.createdAt || history?.created_at || history?.timeStamp || history?.timestamp || history?.date;
                  if (!rawDate) return isEmployee ? "" : "N/A";
                  if (dayjs(rawDate).isValid()) {
                    return dayjs(rawDate).format("DD-MM-YYYY h:mm A");
                  }
                  return String(rawDate);
                })();

                return (
                  <tr key={index}>
                    <td>
                      <span className={`badge ${isEmployee ? "bg-primary-subtle text-primary" : "bg-warning-subtle text-warning"} border`}>
                        {userType}
                      </span>
                    </td>
                    <td className="fw-medium font-monospace">{displayUserId}</td>
                    <td className="fw-medium">{userName}</td>
                    <td>
                      {(() => {
                        const rem = history?.remarks || history?.remark || history?.Comments || "";
                        if (rem && String(rem).trim() !== "-" && String(rem).trim() !== "X" && String(rem).trim() !== "null") {
                          return String(rem).trim();
                        }
                        return "-";
                      })()}
                    </td>
                    <td className="text-muted small">{formattedTimestamp}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
